/**
 * Unified icon renderer supporting both Iconify (static/animated SVG) and
 * Lottie animations (loaded from CDN).
 *
 * Value conventions:
 * - `"prefix:icon-name"` → rendered via @iconify/react (static or animated SVG)
 * - `"prefix:icon-name#ff5733"` → monochrome icon tinted with hex color via CSS color
 * - `"prefix:icon-name#hue120"` → multi-color icon shifted 120° via hue-rotate
 * - `"lottie:https://..."` → rendered via @lottiefiles/dotlottie-react (speed 1×)
 * - `"lottie:https://...:0.5"` → Lottie at half speed
 * - `"lottie:https://...:2"` → Lottie at double speed
 * - `"lottie:https://...:2:180"` → double speed + 180° hue rotation
 */
import { Icon } from "@iconify/react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { memo } from "react";

export type DynamicIconProps = {
  /** Icon identifier: either an Iconify name or a `lottie:<url>` string. */
  icon: string;
  /** Width/height in pixels. Defaults to 24. */
  size?: number;
  /** Additional CSS class applied to the wrapper. */
  className?: string;
};

/** Returns true if the icon value represents a Lottie animation. */
export function isLottieIcon(icon: string): boolean {
  return icon.startsWith("lottie:");
}

// ---------------------------------------------------------------------------
// Icon sets that use `currentColor` (monochrome) vs baked-in colors
// ---------------------------------------------------------------------------
const MONOCHROME_PREFIXES = ["line-md", "gravity-ui", "heroicons", "lucide", "mdi", "tabler"];

export function isMonochromeIcon(icon: string): boolean {
  const prefix = icon.split(":")[0] ?? "";
  return MONOCHROME_PREFIXES.includes(prefix);
}

// ---------------------------------------------------------------------------
// Icon color parsing — `icon-name#ff5733` or `icon-name#hue120`
// ---------------------------------------------------------------------------
export type IconColorInfo = {
  name: string;
  color: string | null; // hex color (e.g. "#ff5733") for monochrome icons
  hue: number; // hue-rotate degrees for multi-color icons (0 = no shift)
};

export function parseIconColor(icon: string): IconColorInfo {
  const hashIdx = icon.indexOf("#");
  if (hashIdx < 0) return { name: icon, color: null, hue: 0 };

  const name = icon.slice(0, hashIdx);
  const suffix = icon.slice(hashIdx + 1);

  // hue-rotate: #hue120
  const hueMatch = suffix.match(/^hue(\d+)$/);
  if (hueMatch) {
    const hue = parseInt(hueMatch[1] ?? "0", 10);
    if (hue >= 0 && hue <= 360) return { name, color: null, hue };
  }

  // hex color: #rgb, #rgba, #rrggbb, or #rrggbbaa
  if (/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{4}$|^[0-9a-fA-F]{6}$|^[0-9a-fA-F]{8}$/.test(suffix)) {
    return { name, color: `#${suffix}`, hue: 0 };
  }

  return { name: icon, color: null, hue: 0 };
}

export function buildIconValue(name: string, color: string | null, hue: number): string {
  if (color) return `${name}#${color.replace(/^#/, "")}`;
  const h = ((hue % 360) + 360) % 360;
  if (h > 0) return `${name}#hue${h}`;
  return name;
}

// ---------------------------------------------------------------------------
// Lottie value parsing
// ---------------------------------------------------------------------------

/**
 * Parse a `lottie:<url>` or `lottie:<url>:<speed>` or
 * `lottie:<url>:<speed>:<hue>` string into its parts.
 *
 * Speed is a multiplier (1 = normal, 0.5 = half, 2 = double).
 * Hue is a CSS hue-rotate angle in degrees (0–360, 0 = no shift).
 * Both trailing segments are optional and default to speed=1, hue=0.
 */
export function parseLottieValue(icon: string): {
  url: string;
  speed: number;
  hue: number;
} {
  const rest = icon.slice("lottie:".length);

  // Try matching two trailing numeric segments: :<speed>:<hue>
  // Guard: the remaining URL must still contain "://" to avoid misinterpreting
  // URL-embedded colons (e.g. ?v=2) as speed/hue separators.
  const twoSuffix = rest.match(/:(\d+\.?\d*):(\d+\.?\d*)$/);
  if (twoSuffix) {
    const speed = Number(twoSuffix[1]);
    const hue = Number(twoSuffix[2]);
    const url = rest.slice(0, rest.length - twoSuffix[0].length);
    if (speed > 0 && speed <= 5 && hue >= 0 && hue <= 360 && url.includes("://")) {
      return { url, speed, hue };
    }
  }

  // Try matching one trailing numeric segment: :<speed>
  const lastColon = rest.lastIndexOf(":");
  if (lastColon > 0) {
    const tail = rest.slice(lastColon + 1);
    const num = Number(tail);
    const url = rest.slice(0, lastColon);
    if (tail.length > 0 && !Number.isNaN(num) && num > 0 && num <= 5 && url.includes("://")) {
      return { url, speed: num, hue: 0 };
    }
  }

  return { url: rest, speed: 1, hue: 0 };
}

/** Extracts the CDN URL from a lottie icon string. */
export function getLottieUrl(icon: string): string {
  return parseLottieValue(icon).url;
}

/**
 * Build a lottie icon string from parts. Omits trailing defaults so
 * `lottie:<url>` stays clean when speed=1 and hue=0.
 */
export function buildLottieValue(url: string, speed: number = 1, hue: number = 0): string {
  const h = ((hue % 360) + 360) % 360;
  if (h !== 0) return `lottie:${url}:${speed}:${h}`;
  if (speed !== 1) return `lottie:${url}:${speed}`;
  return `lottie:${url}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const DynamicIconImpl = ({ icon, size = 24, className }: DynamicIconProps) => {
  if (isLottieIcon(icon)) {
    const { url, speed, hue } = parseLottieValue(icon);
    const hueStyle = hue ? { filter: `hue-rotate(${hue}deg)` } : undefined;
    return (
      <span
        className={`inline-flex shrink-0 items-center justify-center ${className ?? ""}`}
        style={{ width: size, height: size, ...hueStyle }}
      >
        <DotLottieReact
          src={url}
          loop
          autoplay
          speed={speed}
          style={{ width: size, height: size }}
        />
      </span>
    );
  }

  // Regular Iconify icon — may have color/hue suffix
  const { name, color, hue } = parseIconColor(icon);
  const style: React.CSSProperties = {};
  if (color) style.color = color;
  if (hue) style.filter = `hue-rotate(${hue}deg)`;

  if (color || hue) {
    return (
      <span
        className={`inline-flex shrink-0 items-center justify-center ${className ?? ""}`}
        style={{ width: size, height: size, ...style }}
      >
        <Icon icon={name} width={size} height={size} />
      </span>
    );
  }

  return <Icon icon={name} width={size} height={size} className={className} />;
};

export const DynamicIcon = memo(DynamicIconImpl);

export default DynamicIcon;
